import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, MapPin, SlidersHorizontal, X } from 'lucide-react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { backendProviderService } from '@/services/backendProviderService';
import { backendJobService } from '@/services/backendJobService';
import { locationService } from '@/services/locationService';
import { ProviderCard } from '@/components/ui/ProviderCard';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkipLink } from '@/components/ui/SkipLink';
import { SkeletonProviderCard } from '@/components/ui/SkeletonLoaders';
import { EnhancedEmptyState } from '@/components/ui/EnhancedEmptyState';
import { FadeIn } from '@/components/ui/AnimatedComponents';
import type { ServiceProvider } from '@/types';

export function ProvidersPage() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedRating, setSelectedRating] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [services, setServices] = useState<string[]>([]);
  const [locations, setLocations] = useState<string[]>([]);

  useEffect(() => {
    fetchProviders();
    fetchServices();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (services.length > 0 && locations.length > 0) {
      fetchProviders();
    }
  }, [searchTerm, selectedService, selectedLocation]);

  const fetchServices = async () => {
    try {
      const categories = await backendJobService.getJobCategories();
      const serviceNames = categories.map(cat => cat.name);
      setServices(serviceNames);
    } catch (err) {
      setServices(['Plumbing', 'Electrical', 'Carpentry', 'General Repairs']);
    }
  };

  const fetchLocations = async () => {
    try {
      const counties = await locationService.getCounties();
      const locationNames = counties.map(county => county.name);
      setLocations(locationNames);
    } catch (err) {
      setLocations(['Dublin', 'Cork', 'Galway']);
    }
  };

  const fetchProviders = async () => {
    try {
      setLoading(true);
      setError(null);
      const providersData = await backendProviderService.getProviders({
        service: selectedService || undefined,
        city: selectedLocation || undefined,
      });
      setProviders(providersData);
    } catch (err) {
      setError('Failed to load providers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = provider.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.serviceCategories.some((service: string) => service.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesService = !selectedService || provider.serviceCategories.includes(selectedService);
    const matchesLocation = !selectedLocation || provider.location === selectedLocation;
    const matchesRating = !selectedRating || (provider.rating && provider.rating >= parseFloat(selectedRating));
    return matchesSearch && matchesService && matchesLocation && matchesRating;
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedService('');
    setSelectedLocation('');
    setSelectedRating('');
  };

  const hasActiveFilters = searchTerm || selectedService || selectedLocation || selectedRating;

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Service Providers' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SkipLink href="#main-content">Skip to main content</SkipLink>
        <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <SkeletonProviderCard key={i} />
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-4 sm:mt-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Service Providers
            </h1>
            <p className="text-base sm:text-lg text-gray-600">
              Browse verified professionals in your area
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or service..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Filter Toggle (Mobile) */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              icon={<SlidersHorizontal className="w-4 h-4" />}
              className="sm:hidden"
            >
              Filters
            </Button>

            {/* Quick Filters (Desktop) */}
            <div className="hidden sm:flex gap-2">
              <select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-700"
              >
                <option value="">All Services</option>
                {services.map(service => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </select>

              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-700"
              >
                <option value="">All Locations</option>
                {locations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="md"
                  onClick={handleClearFilters}
                  icon={<X className="w-4 h-4" />}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Mobile Filters Dropdown */}
          {showFilters && (
            <div className="sm:hidden mt-3 p-4 bg-gray-50 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                <select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  <option value="">All Services</option>
                  {services.map(service => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  <option value="">All Locations</option>
                  {locations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={handleClearFilters}
                  icon={<X className="w-4 h-4" />}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Results Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <p className="text-sm text-gray-600">
            <span className="font-medium text-gray-900">{filteredProviders.length}</span> provider{filteredProviders.length !== 1 ? 's' : ''} found
          </p>

          <select
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white min-h-touch"
            defaultValue="recommended"
            aria-label="Sort providers"
          >
            <option value="recommended">Sort by: Recommended</option>
            <option value="rating">Highest Rated</option>
            <option value="reviews">Most Reviews</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
          </select>
        </div>

        {/* Error State */}
        {error && (
          <Card padding="lg" className="mb-6 border-error-200 bg-error-50">
            <p className="text-error-700">{error}</p>
          </Card>
        )}

        {/* Provider Grid */}
        {filteredProviders.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProviders.map((provider, index) => (
              <FadeIn key={provider.id} delay={index * 50}>
                <ProviderCard
                  provider={provider}
                  onRequestQuote={(id) => navigate(`/providers/${id}/quote`)}
                  onViewProfile={(id) => navigate(`/providers/${id}`)}
                  onMessage={(id) => navigate(`/chat?provider=${id}`)}
                />
              </FadeIn>
            ))}
          </div>
        ) : (
          <EnhancedEmptyState
            icon={Search}
            title="No providers found"
            description="We couldn't find any providers matching your criteria. Try adjusting your filters or search terms."
            action={
              hasActiveFilters 
                ? { label: 'Clear All Filters', onClick: handleClearFilters, icon: <X className="w-4 h-4" /> }
                : undefined
            }
            size="md"
            illustration="pattern"
          />
        )}

        {/* Load More (if pagination needed) */}
        {filteredProviders.length > 0 && filteredProviders.length >= 12 && (
          <div className="mt-8 text-center">
            <Button variant="outline" size="lg">
              Load More Providers
            </Button>
          </div>
        )}
      </main>

      {/* CTA Section */}
      <div className="bg-primary-600 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Can't find what you're looking for?
          </h2>
          <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
            Post your job and let professionals come to you with custom quotes
          </p>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => navigate('/create-job')}
          >
            Post a Job - It's Free
          </Button>
        </div>
      </div>
    </div>
  );
}
